/** @file The open project's import entry points (Import PSD, Add PNG images) and the dialog they open. */
import type { Translate } from '@pose2d/i18n/types';
import { useEffect, useMemo, useRef } from 'react';
import { createImportSession } from './createImportSession';
import { FilePicker } from './FilePicker';
import { IMPORT_ACCEPT } from './importAccept.data';
import { ImportDialog } from './ImportDialog';
import type { ImportTarget } from './importTypes';
import { openPsdInWorker } from './openPsdInWorker';
import { restPorts } from './restPorts';

const resolvedAtOnce = (): Promise<void> => Promise.resolve();

/** `confirmed` (E9.6 passes whenConfirmed) resolves once the editor's document reached the import's rev; the default resolves at once. The session is built once and reads only the latest `confirmed`, never its identity. */
export function ImportButtons({ t, projectId, confirmed }: { t: Translate; projectId: string; confirmed?: (rev: number) => Promise<void> }) {
  const wait = useRef(confirmed);
  wait.current = confirmed;
  const session = useMemo(
    () => createImportSession({ ports: restPorts((rev) => (wait.current ?? resolvedAtOnce)(rev)), openPsd: (bytes) => openPsdInWorker(bytes), afterImport: () => Promise.resolve() }),
    [],
  );
  useEffect(() => () => { session.dispose(); }, [session]);
  const target: ImportTarget = { kind: 'project', projectId };
  return (
    <div className="import-buttons">
      <FilePicker label={t('psdImport.choosePsd')} accept={IMPORT_ACCEPT.psd} multiple={false} onFiles={(files) => { const [file] = files; if (file !== undefined) void session.openPsd(file, target); }} />
      <FilePicker label={t('psdImport.choosePngs')} accept={IMPORT_ACCEPT.png} multiple onFiles={(files) => { void session.openPngs(files, target); }} />
      <ImportDialog t={t} session={session} />
    </div>
  );
}
